import { httpApi } from '@api/http.api';
import { CONTENT_TYPE_FORM_DATA_HEADER, NO_REQUEST_TIMEOUT } from '@constants/api';
import type {
    CommonTablePaginatedResponse,
    CommonTableRequest,
} from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { CSV_TYPE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import type { CsvUploadStateResponse as SharedCsvUploadStateResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import type { AxiosResponse } from 'axios';

export type CrewUsersRequest = CommonTableRequest;

export interface CrewUser {
    id: number;
    employeeId: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    primaryEmail: string | null;
    secondaryEmail: string | null;
    phone: string | null;
    nationalityId: number | null;
    nationalityName: string | null;
    genderId: number | null;
    genderName: string | null;
    isActive: boolean;
    rankId: number | null;
    rankName: string | null;
    rankShortCode: string | null;
    rankDepartmentId: number | null;
    rankDepartmentName: string | null;
    rankDepartmentShortCode: string | null;
    vesselId: number | null;
    vesselName: string | null;
    vesselIMONumber: string | null;
    rankSince: string | null;
    signOn: string | null;
    plannedSignOff: string | null;
    actualSignOff: string | null;
    nextPlannedSignOn: string | null;
    isOnboard: boolean;
    nextPlannedRankId: number | null;
    nextPlannedRankName: string | null;
    nextPlannedRankShortCode: string | null;
    nextPlannedVesselId: number | null;
    nextPlannedVesselName: string | null;
    nextPlannedVesselIMONumber: string | null;
    manningOfficeId: number | null;
    manningOfficeName: string | null;
    crewingTeamId: number | null;
    crewingTeamName: string | null;

    joinedSince: string | null;
    lastChanged: string | null;
    createdAtUTC: string | null;
    lastActivityUTC: string | null;
    requirementsStartDate: string | null;
}

export interface CrewUsersResultsDescription {
    [k: string]: unknown;
}

export interface CsvUploadStateResponse extends SharedCsvUploadStateResponse {
    csvType: typeof CSV_TYPE.CREW_USER;
}

export interface CrewUsersResponse {
    data: CrewUser[];
    pagination: CommonTablePaginatedResponse;
    resultsDescription: CrewUsersResultsDescription;
}

export interface CrewUserPayload {
    employeeId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    nationalityId: number;
    genderId: number | null;
    primaryEmail: string;
    secondaryEmail: string | null;
    phone: string | null;
    isActive: boolean;
    vesselRankId: number;
    clientVesselId: number | null;
    signOn: string | null;
    plannedSignOff: string | null;
    actualSignOff: string | null;
    nextPlannedSignOn: string | null;
    nextPlannedRankId: number | null;
    nextPlannedVesselId: number | null;
    manningOfficeId: number | null;
    crewingTeamId: number | null;
    joinedSince: string | null;
    requirementsStartDate: string | null;
    rankSince: string | null;
}

export type CreateCrewUserRequest = CrewUserPayload;
export type UpdateCrewUserRequest = CrewUserPayload;

export const getApiCrewUsers = (
    clientId: number,
    tenantAlias: string,
    payload: CrewUsersRequest,
): Promise<CrewUsersResponse> =>
    httpApi
        .post<CrewUsersResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/search`, payload)
        .then(({ data }) => data);

export const createApiCrewUser = (
    clientId: number,
    tenantAlias: string,
    payload: CreateCrewUserRequest,
): Promise<CrewUser> =>
    httpApi
        .post<CrewUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users`, payload)
        .then(({ data }) => data);

export const updateApiCrewUser = (
    clientId: number,
    tenantAlias: string,
    id: number,
    payload: UpdateCrewUserRequest,
): Promise<CrewUser> =>
    httpApi
        .put<CrewUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/${id}`, payload)
        .then(({ data }) => data);

export const deleteApiCrewUser = (clientId: number, tenantAlias: string, id: number): Promise<CrewUser> =>
    httpApi
        .delete<CrewUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/${id}`)
        .then(({ data }) => data);

export const uploadApiCrewUsersCsv = (clientId: number, tenantAlias: string, formData: FormData): Promise<string> =>
    httpApi
        .post(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/upload-csv`, formData, {
            headers: CONTENT_TYPE_FORM_DATA_HEADER,
            timeout: NO_REQUEST_TIMEOUT,
        })
        .then(({ data }) => data);

export const getApiCrewUsersCsvUpload = (
    clientId: number,
    tenantAlias: string,
    id: string,
): Promise<CsvUploadStateResponse> =>
    httpApi
        .get<CsvUploadStateResponse>(
            `admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/upload-csv/${id}`,
        )
        .then(({ data }) => data);

export const downloadApiCrewUsersCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/download-csv`, {
            responseType: 'blob',
        })
        .then((res) => res);

export const downloadApiCrewUsersExampleCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/crew-users/example-csv`, { responseType: 'blob' })
        .then((res) => res);
